import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../../lib/api';
import { fileToResizedDataURL } from '../../lib/image';
import { useToasts } from '../../lib/toast';
import type { MessagingCustomer, CampaignRow } from '@taj/types';

type Channel = 'both' | 'sms' | 'email';
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

export function MessagingManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [audience, setAudience] = useState<MessagingCustomer[]>([]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [channel, setChannel] = useState<Channel>('both');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [photo, setPhoto] = useState('');
  const [sending, setSending] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [cMsg, setCMsg] = useState('');
  const [cSubject, setCSubject] = useState('');
  const [cChannel, setCChannel] = useState<Channel>('both');
  const [cDate, setCDate] = useState('');
  const [cTime, setCTime] = useState('10:00');
  const [cRecur, setCRecur] = useState<'once' | 'weekly' | 'monthly'>('once');

  const reload = () => api.messagingAudience().then(setAudience).catch((e) => push(e.message, 'warn'));
  const reloadCampaigns = () => api.listCampaigns().then(setCampaigns).catch(() => {});
  useEffect(() => {
    api.messagingAudience().then((a) => { setAudience(a); setSel(new Set(a.map((c) => c.id))); }).catch((e) => push(e.message, 'warn'));
    reloadCampaigns();
  }, [push]);

  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = audience.length > 0 && audience.every((c) => sel.has(c.id));

  const addContact = async () => {
    if (!newPhone.trim() && !newEmail.trim()) { push('Entre un numéro ou un email', 'warn'); return; }
    try {
      const c = await api.addContact({ name: newName.trim() || undefined, phone: newPhone.trim() || undefined, email: newEmail.trim() || undefined });
      setNewName(''); setNewPhone(''); setNewEmail('');
      await reload();
      setSel((s) => new Set(s).add(c.id));
      push('✅ Contact ajouté');
    } catch (e: any) { push(e.message, 'warn'); }
  };
  const removeContact = async (c: MessagingCustomer) => {
    if (!confirm(`Supprimer ${c.name || c.phone || c.email} ?`)) return;
    try { await api.deleteContact(c.id); setAudience((l) => l.filter((x) => x.id !== c.id)); } catch (e: any) { push(e.message, 'warn'); }
  };

  const pickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setPhoto(await fileToResizedDataURL(f, 900, 0.8)); } catch { push('Photo illisible', 'warn'); }
  };

  const send = async () => {
    if (!message.trim() && !photo) { push('Écris un message', 'warn'); return; }
    const selected = audience.filter((c) => sel.has(c.id));
    const phones = channel !== 'email' ? selected.map((c) => c.phone).filter((p): p is string => !!p) : [];
    const emails = channel !== 'sms' ? selected.map((c) => c.email).filter((e): e is string => !!e) : [];
    if (phones.length === 0 && emails.length === 0) { push('Aucun destinataire (selon le canal choisi)', 'warn'); return; }
    if (!confirm(`Envoyer à ${phones.length} SMS + ${emails.length} email(s) ?`)) return;
    setSending(true);
    try {
      const r = await api.sendMessages({ message: message.trim(), subject: subject.trim() || undefined, phones, emails, photo: photo || undefined });
      if (r.sent > 0) push(`✅ ${r.sent} envoyé(s)${r.failed ? ` · ${r.failed} échec(s)` : ''}`);
      else if (r.simulated > 0) push(`⚠️ ${r.simulated} simulé(s) — configure Twilio (SMS) / Resend (email) pour envoyer en vrai`, 'warn');
      else push(`${r.failed} échec(s)`, 'warn');
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setSending(false); }
  };

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const rows: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      const contacts = rows.map((r) => {
        const o: { name?: string; phone?: string; email?: string } = {};
        for (const k of Object.keys(r)) {
          const nk = norm(k);
          if (nk.includes('nom') || nk === 'name') o.name = String(r[k]).trim();
          else if (nk.includes('tel') || nk.includes('phone') || nk.includes('numero') || nk.includes('mobile')) o.phone = String(r[k]).trim();
          else if (nk.includes('mail')) o.email = String(r[k]).trim();
        }
        return o;
      }).filter((c) => c.phone || c.email);
      if (contacts.length === 0) { push('Aucun contact trouvé (colonnes : Nom, Téléphone, Email)', 'warn'); return; }
      const r = await api.importContacts(contacts);
      push(`✅ ${r.added} contact(s) importé(s)${r.skipped ? ` · ${r.skipped} ignoré(s)` : ''}`);
      await reload();
    } catch { push('Fichier illisible', 'warn'); }
  };

  const exportCsv = () => {
    const header = 'Nom;Téléphone;Email\n';
    const body = audience.map((c) => `${(c.name ?? '').replace(/;/g, ',')};${c.phone ?? ''};${c.email ?? ''}`).join('\n');
    const blob = new Blob(['﻿' + header + body], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'clients-taj-fusion.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const addCampaign = async () => {
    if (!cMsg.trim()) { push('Écris le message de la campagne', 'warn'); return; }
    if (!cDate) { push('Choisis une date', 'warn'); return; }
    const scheduledAt = new Date(`${cDate}T${cTime || '10:00'}:00`).toISOString();
    try {
      await api.createCampaign({ message: cMsg.trim(), subject: cSubject.trim() || undefined, channel: cChannel, scheduledAt, recurrence: cRecur });
      push('✅ Campagne programmée');
      setCMsg(''); setCSubject(''); setCDate('');
      reloadCampaigns();
    } catch (e: any) { push(e.message, 'warn'); }
  };
  const delCampaign = async (id: string) => {
    if (!confirm('Supprimer cette campagne ?')) return;
    try { await api.deleteCampaign(id); setCampaigns((l) => l.filter((x) => x.id !== id)); } catch (e: any) { push(e.message, 'warn'); }
  };
  const RECUR_LABEL: Record<string, string> = { once: 'Une fois', weekly: 'Chaque semaine', monthly: 'Chaque mois' };
  const CHAN_LABEL: Record<string, string> = { both: 'SMS + Email', sms: 'SMS', email: 'Email' };

  const seg = (v: Channel, label: string) => (
    <button onClick={() => setChannel(v)} style={{ padding: '8px 14px', borderRadius: 10, fontWeight: 800, cursor: 'pointer', border: channel === v ? 'none' : '1px solid #ddd', background: channel === v ? 'var(--gold)' : '#f6f6f6', color: channel === v ? '#fff' : '#555' }}>{label}</button>
  );

  const selected = audience.filter((c) => sel.has(c.id));
  const nSms = channel !== 'email' ? selected.filter((c) => c.phone).length : 0;
  const nMail = channel !== 'sms' ? selected.filter((c) => c.email).length : 0;

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>💬 Campagne SMS</div>
      </div>
      <div style={{ fontSize: '.82rem', color: 'var(--ink-faint)', marginBottom: 12, maxWidth: 640 }}>
        Envoie une promo/annonce par <b>SMS</b> et/ou <b>Email</b> à ta base de clients (rassemblée automatiquement +
        les numéros que tu ajoutes). La photo part en <b>lien</b> dans le SMS, et <b>intégrée</b> dans l'email.
      </div>

      {/* Composer */}
      <div className="cc" style={{ maxWidth: 640, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {seg('both', '📱📧 SMS + Email')}{seg('sms', '📱 SMS')}{seg('email', '📧 Email')}
        </div>
        {channel !== 'sms' && (
          <input className="inp" style={{ width: '100%', marginBottom: 8 }} placeholder="Objet de l'email (ex. Offre du week-end)" value={subject} onChange={(e) => setSubject(e.target.value)} />
        )}
        <textarea className="inp" style={{ width: '100%', minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Ton message… (ex. « 🎉 -20% ce week-end sur les menus ! »)" value={message} onChange={(e) => setMessage(e.target.value)} />
        {channel !== 'email' && <div style={{ fontSize: '.72rem', color: 'var(--ink-faint)', marginTop: 4 }}>{message.length} caractères · {Math.ceil((message.length || 0) / 153) || 1} SMS</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <label className="cf-reprint" style={{ width: 'auto', padding: '9px 12px', cursor: 'pointer' }}>
            📷 Ajouter une photo
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={pickPhoto} />
          </label>
          {photo && <img src={photo} style={{ height: 46, borderRadius: 8, objectFit: 'cover' }} />}
          {photo && <button className="cr-btn del" title="Retirer" onClick={() => setPhoto('')}>🗑</button>}
          <button className="btn-gold" style={{ width: 'auto', padding: '11px 22px', marginLeft: 'auto' }} disabled={sending} onClick={send}>
            📨 Envoyer ({nSms} SMS · {nMail} mail)
          </button>
        </div>
      </div>

      {/* Base clients */}
      <div className="cc" style={{ maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.15rem', flex: 1 }}>👥 Clients ({audience.length})</div>
          <label className="cf-reprint" style={{ width: 'auto', padding: '7px 10px', cursor: 'pointer', fontSize: '.8rem' }}>
            📥 Importer<input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={onImportFile} />
          </label>
          <button className="cf-reprint" style={{ width: 'auto', padding: '7px 10px', fontSize: '.8rem' }} onClick={exportCsv}>📤 Exporter</button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.8rem', fontWeight: 800, color: 'var(--ink-dim)', cursor: 'pointer' }}>
            <input type="checkbox" checked={allSelected} onChange={(e) => setSel(e.target.checked ? new Set(audience.map((c) => c.id)) : new Set())} /> Tout
          </label>
        </div>

        {/* Ajouter un contact manuellement */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, paddingBottom: 10, borderBottom: '1px dashed var(--gold-line)' }}>
          <input className="inp" style={{ flex: '1 1 90px' }} placeholder="Nom" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addContact()} />
          <input className="inp" style={{ width: 130 }} inputMode="tel" placeholder="📞 Numéro" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addContact()} />
          <input className="inp" style={{ flex: '1 1 130px' }} inputMode="email" placeholder="📧 Email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addContact()} />
          <button className="btn-gold" style={{ width: 'auto', padding: '9px 14px' }} onClick={addContact}>＋</button>
        </div>

        {audience.length === 0 && <div style={{ fontSize: '.85rem', color: 'var(--ink-faint)' }}>Aucun client. Ajoute un contact ci-dessus, ou ils s'ajouteront avec les commandes.</div>}
        {audience.map((c) => (
          <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}>
            <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{c.name || '—'}</div>
              <div style={{ fontSize: '.76rem', color: 'var(--ink-dim)' }}>{c.phone ? `📞 ${c.phone}` : ''}{c.phone && c.email ? ' · ' : ''}{c.email ? `📧 ${c.email}` : ''}</div>
            </div>
            {c.points > 0 && <span style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--green)' }}>{c.points} pts</span>}
            <button className="cr-btn del" title="Supprimer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeContact(c); }}>🗑</button>
          </label>
        ))}
      </div>

      {/* Campagnes programmées */}
      <div className="cc" style={{ maxWidth: 640, marginTop: 16 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.15rem', marginBottom: 6 }}>⏰ Campagnes automatiques</div>
        <div style={{ fontSize: '.8rem', color: 'var(--ink-faint)', marginBottom: 10 }}>Programme un message envoyé automatiquement à toute ta base, à la date et l'heure choisies.</div>

        {cChannel !== 'sms' && <input className="inp" style={{ width: '100%', marginBottom: 8 }} placeholder="Objet (email)" value={cSubject} onChange={(e) => setCSubject(e.target.value)} />}
        <textarea className="inp" style={{ width: '100%', minHeight: 70, resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }} placeholder="Message de la campagne…" value={cMsg} onChange={(e) => setCMsg(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <select className="inp" style={{ width: 'auto' }} value={cChannel} onChange={(e) => setCChannel(e.target.value as Channel)}>
            <option value="both">📱📧 SMS + Email</option><option value="sms">📱 SMS</option><option value="email">📧 Email</option>
          </select>
          <input className="inp" type="date" style={{ width: 'auto' }} value={cDate} onChange={(e) => setCDate(e.target.value)} />
          <input className="inp" type="time" style={{ width: 'auto' }} value={cTime} onChange={(e) => setCTime(e.target.value)} />
          <select className="inp" style={{ width: 'auto' }} value={cRecur} onChange={(e) => setCRecur(e.target.value as 'once' | 'weekly' | 'monthly')}>
            <option value="once">Une fois</option><option value="weekly">Chaque semaine</option><option value="monthly">Chaque mois</option>
          </select>
          <button className="btn-gold" style={{ width: 'auto', padding: '9px 16px' }} onClick={addCampaign}>⏰ Programmer</button>
        </div>

        {campaigns.length === 0 && <div style={{ fontSize: '.85rem', color: 'var(--ink-faint)' }}>Aucune campagne programmée.</div>}
        {campaigns.map((c) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap', opacity: c.active ? 1 : 0.5 }}>
            <div style={{ flex: '1 1 200px', minWidth: 160 }}>
              <div style={{ fontWeight: 700 }}>{c.message.slice(0, 60)}{c.message.length > 60 ? '…' : ''}</div>
              <div style={{ fontSize: '.76rem', color: 'var(--ink-faint)' }}>📅 {new Date(c.scheduledAt).toLocaleString('fr-FR')} · {RECUR_LABEL[c.recurrence]} · {CHAN_LABEL[c.channel]}{c.active ? '' : ' · terminée'}</div>
            </div>
            <button className="cr-btn del" title="Supprimer" onClick={() => delCampaign(c.id)}>🗑</button>
          </div>
        ))}
      </div>
    </div>
  );
}
